import { UploadedFile } from "./types";

interface createMediaVariables {
  variables: {
    fileName: string;
    bucketName: string;
    type: string;
    tag: string[];
    accountId: string;
    designProjectId?: string;
  };
}

interface UploadFilesProps {
  accountId: string;
  createMedia: (arg: createMediaVariables) => Promise<any>;
  acceptedFiles: File[];
  tags?: string[];
  acceptOneFileOnly?: boolean;
  designProjectId?: string;
}

interface UploadFileProps {
  accountId: string;
  createMedia: (arg: createMediaVariables) => Promise<any>;
  file: File;
  tags?: string[];
  designProjectId?: string;
}

// Example of using uploadFiles with react-dropzone:
//
// const { getRootProps, getInputProps } = useDropzone({
//   accept: {
//     "image/jpeg": [],
//     "image/png": [],
//   },
//   onDrop: async (acceptedFiles) => {
//     const newUploadedFiles = await uploadFiles({
//       accountId: accountId,
//       createMedia: createMedia,
//       acceptedFiles: acceptedFiles,
//     });
//   },
// });

export const uploadFile = async ({
  accountId,
  createMedia,
  file,
  tags,
  designProjectId,
}: UploadFileProps): Promise<UploadedFile> => {
  const { data: createMediaRecordData } = await createMedia({
    variables: {
      fileName: file.name,
      bucketName: process.env.NEXT_PUBLIC_GOOGLE_CLOUD_STORAGE_BUCKET || "",
      type: file.type,
      tag: tags || ["deliverable"],
      accountId: accountId || "",
      ...(designProjectId ? { designProjectId } : {}),
    },
  });
  const id = createMediaRecordData?.createMedia.id;
  const signedUrl = createMediaRecordData?.createMedia.url;

  if (!id || !signedUrl) {
    throw new Error("Error creating media record and signed url");
  }

  const googleCloudStorageResponse = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": "application/octet-stream",
    },
  });

  if (!googleCloudStorageResponse.ok) {
    throw new Error("Error uploading file");
  }

  return {
    id: id,
    fileName: file.name,
    src: URL.createObjectURL(file),
  };
};

export const uploadFiles = async ({
  accountId,
  createMedia,
  acceptedFiles,
  tags,
  acceptOneFileOnly,
  designProjectId,
}: UploadFilesProps) => {
  if (acceptOneFileOnly && acceptedFiles.length > 1) {
    throw new Error("Only one file can be uploaded at a time");
  }
  const newUploadedFiles: UploadedFile[] = [];

  try {
    await Promise.all(
      acceptedFiles.map(async (acceptedFile: File) => {
        const uploadedFile = await uploadFile({
          accountId,
          createMedia,
          file: acceptedFile,
          tags,
          designProjectId,
        });

        newUploadedFiles.push(uploadedFile);
      }),
    );
  } catch (error) {
    console.error(error);
  }

  return newUploadedFiles;
};
